import { store } from '@/store';
import { type IntegrationEndpoint } from '@/store/slices/integrationSlice';
import { envConfig } from './env-config';
import { IntegrationTokenManager } from './integration-token-manager';

// Type for API response data
type ApiResponseData = Record<string, unknown>;

// Type for request data
type RequestData = Record<string, unknown>;

/**
 * Integration API Client
 * Handles API calls to integration partner endpoints using the stored access token
 */
export class IntegrationApiClient {
  /**
   * Make an authenticated API call to an integration partner endpoint
   */
  static async callEndpoint(
    endpoint: IntegrationEndpoint,
    data?: RequestData,
    options?: RequestInit
  ): Promise<{ success: boolean; data?: ApiResponseData; error?: string }> {
    try {
      // Get the valid integration token
      const token = IntegrationTokenManager.getValidToken();

      if (!token) {
        console.error('❌ No valid integration token available');
        return {
          success: false,
          error:
            'No valid integration token available. Please authenticate first.',
        };
      }

      // Check if token is expiring soon
      if (IntegrationTokenManager.isTokenExpiringSoon()) {
        console.warn('⚠️ Integration token is expiring soon');
      }

      // Build URL with query parameters for GET requests
      let url = endpoint.endpoint_url;
      if (endpoint.method === 'GET' && data) {
        const queryParams = new URLSearchParams();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      // Prepare headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options?.headers,
      };

      // Prepare request options with CORS configuration
      const requestOptions: RequestInit = {
        method: endpoint.method,
        headers,
        mode: 'cors', // Enable CORS
        ...options,
      };

      // Add body for non-GET requests
      if (endpoint.method !== 'GET' && data) {
        requestOptions.body = JSON.stringify(data);
      }

      // Make the API call
      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Integration API call failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });

        return {
          success: false,
          error: `API call failed: ${response.status} ${response.statusText}`,
        };
      }

      // Parse response
      const responseData = (await response.json()) as ApiResponseData;

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      console.error('❌ Integration API call error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Find and call an endpoint by action and environment
   */
  static async callEndpointByAction(
    action: string,
    data?: RequestData,
    options?: RequestInit,
    environment: string = envConfig.env
  ): Promise<{ success: boolean; data?: ApiResponseData; error?: string }> {
    const state = store.getState();
    const endpoints = state.integration.endpoints;

    // Find endpoint by action and environment
    const endpoint = endpoints.find(
      (ep) => ep.action === action && ep.environment === environment
    );

    if (!endpoint) {
      console.error('❌ Endpoint not found:', { action, environment });
      return {
        success: false,
        error: `No endpoint found for action: ${action} and environment: ${environment}`,
      };
    }

    return this.callEndpoint(endpoint, data, options);
  }

  /**
   * Get all available endpoints
   */
  static getAvailableEndpoints(): IntegrationEndpoint[] {
    const state = store.getState();
    return state.integration.endpoints;
  }

  /**
   * Get available endpoints for a specific action
   */
  static getEndpointsForAction(action: string): IntegrationEndpoint[] {
    const state = store.getState();
    return state.integration.endpoints.filter(
      (ep) => ep.action === action && ep.environment === envConfig.env
    );
  }

  /**
   * Get available endpoints for a specific environment
   */
  static getEndpointsForEnvironment(
    environment: string = envConfig.env
  ): IntegrationEndpoint[] {
    const state = store.getState();
    return state.integration.endpoints.filter(
      (ep) => ep.environment === environment
    );
  }

  /**
   * Check if API calls can be made
   */
  static canMakeApiCalls(): boolean {
    const hasValidToken = IntegrationTokenManager.isTokenValid();
    const state = store.getState();
    const hasEndpoints = state.integration.endpoints.length > 0;
    const hasPartner = !!state.integration.partner;

    return hasValidToken && hasEndpoints && hasPartner;
  }

  /**
   * Get integration status
   */
  static getStatus(): {
    hasValidToken: boolean;
    hasEndpoints: boolean;
    hasPartner: boolean;
    canMakeApiCalls: boolean;
    tokenInfo: ReturnType<typeof IntegrationTokenManager.getTokenInfo>;
  } {
    const hasValidToken = IntegrationTokenManager.isTokenValid();
    const state = store.getState();
    const hasEndpoints = state.integration.endpoints.length > 0;
    const hasPartner = !!state.integration.partner;
    const canMakeApiCalls = hasValidToken && hasEndpoints && hasPartner;

    return {
      hasValidToken,
      hasEndpoints,
      hasPartner,
      canMakeApiCalls,
      tokenInfo: IntegrationTokenManager.getTokenInfo(),
    };
  }
}
